'use client'

/**
 * Transient confirmations and errors (T-WEB-08, T-A11Y-01).
 *
 * Two live regions, both rendered unconditionally so assistive tech is
 * already listening when text arrives: a polite one for "Saved", which must
 * not interrupt a screen reader mid-sentence, and an assertive one for
 * errors, which should. Each toast can be dismissed, and its timer pauses
 * while it is hovered or focused (WCAG 2.2.1) — five seconds is not enough
 * for everyone to read a sentence.
 */
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useShellText} from '@/components/shell/ShellText'
import {X} from 'lucide-react'
import './shell/shell.css'

type Tone = 'info' | 'error'

interface Toast {
  id: number
  text: string
  tone: Tone
}

interface ToastApi {
  show: (text: string, tone?: Tone) => void
}

const ToastContext = createContext<ToastApi>({show: () => {}})

export function useToast(): ToastApi {
  return useContext(ToastContext)
}

const VISIBLE_MS = 5000
/** Errors carry a reason and often a next step; they stay up longer. */
const ERROR_VISIBLE_MS = 8000

export function ToastProvider({children}: {children: React.ReactNode}) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback((text: string, tone: Tone = 'info') => {
    const id = Date.now() + Math.random()
    setToasts((current) => [...current, {id, text, tone}])
  }, [])

  const api = useMemo(() => ({show}), [show])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="u-toasts">
        <div role="status" aria-live="polite" className="u-toasts__region">
          {toasts
            .filter((toast) => toast.tone !== 'error')
            .map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
        </div>
        <div role="alert" aria-live="assertive" className="u-toasts__region">
          {toasts
            .filter((toast) => toast.tone === 'error')
            .map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
        </div>
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({toast, onDismiss}: {toast: Toast; onDismiss: (id: number) => void}) {
  const t = useShellText('common')
  const [paused, setPaused] = useState(false)
  // Time left survives a pause, so hovering doesn't restart the clock.
  const remaining = useRef(toast.tone === 'error' ? ERROR_VISIBLE_MS : VISIBLE_MS)

  useEffect(() => {
    if (paused) return
    const started = Date.now()
    const timer = setTimeout(() => onDismiss(toast.id), remaining.current)
    return () => {
      clearTimeout(timer)
      remaining.current -= Date.now() - started
    }
  }, [paused, toast.id, onDismiss])

  return (
    <div
      className={`u-toast${toast.tone === 'error' ? ' u-toast--error' : ''}`}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <p>{toast.text}</p>
      <button
        type="button"
        className="u-toast__close"
        aria-label={t('close')}
        onClick={() => onDismiss(toast.id)}
      >
        <X aria-hidden="true" size={16} />
      </button>
    </div>
  )
}
